import { AiFeaturePreset } from '../../../../models/AiFeaturePreset'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useGetFeaturesQuery, useUpdateInputParametersMutation } from '../../../../store/ai/ai'
import { useCallback, useEffect, useState } from 'react'
import { AiFeature, TemplateInputParameters } from '../../../../models/AiFeature'

const useGlobalPresetInputParameters = (preset?: AiFeaturePreset | null) => {
  const accountId = useAppSelector(selectAccountId)

  const { data: aiFeatures, isLoading: featuresLoading } = useGetFeaturesQuery(
    {
      accountId,
      pageRequest: { page: 0, size: 500 },
      includeRestricted: true,
    },
    { refetchOnMountOrArgChange: true },
  )

  const [updateInputParameter] = useUpdateInputParametersMutation()

  const [presetAiFeatures, setPresetAiFeatures] = useState<AiFeature[] | null>(null)
  const [updateLoading, setUpdateLoading] = useState<boolean>(false)

  useEffect(() => {
    if (!preset || !aiFeatures?.data) {
      return
    }
    setPresetAiFeatures(aiFeatures.data.filter((feature) => feature.preset?.id === preset.id))
  }, [aiFeatures, preset])

  const updateInputParameters = useCallback(
    async (inputParameters: { [key: string]: any }) => {
      if (!presetAiFeatures) {
        return
      }
      setUpdateLoading(true)
      for (const updatableFeature of presetAiFeatures) {
        try {
          await updateInputParameter({
            aiFeatureId: updatableFeature.id,
            accountId,
            payload: {
              inputParameters,
            },
          })
        } catch (e) {
          console.error(e)
        }
      }
      setUpdateLoading(false)
    },
    [accountId, updateInputParameter, presetAiFeatures],
  )

  return { isLoading: featuresLoading, presetAiFeatures, updateInputParameters, updateLoading }
}

export const useGlobalFormPresetInputParameters = (preset?: AiFeaturePreset | null) => {
  const { isLoading, presetAiFeatures, updateInputParameters, updateLoading } = useGlobalPresetInputParameters(preset)

  const [inputParameters, setInputParameters] = useState<{ [key: string]: any } | null>(null)

  useEffect(() => {
    if (!preset || isLoading || !presetAiFeatures) {
      return
    }
    const aiFeature = presetAiFeatures.find((value) => !!value.inputParameters)
    setInputParameters(aiFeature?.inputParameters ?? {})
  }, [presetAiFeatures, preset, isLoading])

  return {
    presetInputParameters: inputParameters,
    updateInputParameters,
    updateLoading,
  }
}

export const useGlobalTemplatePresetInputParameters = (preset?: AiFeaturePreset | null) => {
  const { isLoading, presetAiFeatures, updateInputParameters, updateLoading } = useGlobalPresetInputParameters(preset)

  const [inputParameters, setInputParameters] = useState<TemplateInputParameters | null>(null)
  const [defaultInputParameters, setDefaultInputParameters] = useState<TemplateInputParameters | null>(null)

  useEffect(() => {
    if (!preset || isLoading || !presetAiFeatures) {
      return
    }
    const defaultTemplate = preset.inputParametersSchema?.properties?.template.default
    const aiFeature = presetAiFeatures.find((value) => !!value.inputParameters)
    const example_1 = aiFeature?.inputParameters?.example_1 || ''
    const example_2 = aiFeature?.inputParameters?.example_2 || ''
    const template = aiFeature?.inputParameters?.template || defaultTemplate
    setInputParameters({ example_1, example_2, template })
    setDefaultInputParameters({ example_1: '', example_2: '', template: defaultTemplate })
  }, [presetAiFeatures, preset, isLoading])

  return {
    presetInputParameters: inputParameters,
    presetDefaultInputParameters: defaultInputParameters,
    updateInputParameters,
    updateLoading,
  }
}
